import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { Trophy, ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";

import { Button } from "@/components/Button/Button";

interface TeamStanding {
  position: number;
  team: {
    id: number;
    name: string;
    shortName: string;
    crest: string;
  };
  playedGames: number;
  won: number;
  draw: number;
  lost: number;
  points: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  form: string;
}

const ITEMS_PER_PAGE = 5;

export const PremierLeague = () => {
  const [open, setOpen] = useState(false);
  const [standings, setStandings] = useState<TeamStanding[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!open) return;

    const fetchStandings = async () => {
      if (standings.length > 0) return; // Already fetched

      try {
        setLoading(true);

        // Using free API from api-football.com via RapidAPI (100 requests/day free)
        // Alternative: Use TheSportsDB.com which is completely free
        const response = await fetch(
          "https://www.thesportsdb.com/api/v1/json/123/lookuptable.php?l=4328&s=2026-2027"
        );

        if (!response.ok) {
          throw new Error("Failed to fetch standings");
        }
        const data = await response.json();

        // Transform TheSportsDB data to our format
        if (data.table) {
          const transformedData: TeamStanding[] = data.table.map(
            (team: {
              intRank: string;
              idTeam: string;
              strTeam: string;
              strBadge?: string;
              strTeamBadge?: string;
              strForm?: string;
              intPlayed: string;
              intWin: string;
              intDraw: string;
              intLoss: string;
              intPoints: string;
              intGoalsFor: string;
              intGoalsAgainst: string;
              intGoalDifference: string;
            }) => ({
              position: parseInt(team.intRank),
              team: {
                id: parseInt(team.idTeam),
                name: team.strTeam,
                shortName: team.strTeam,
                crest: team.strBadge || team.strTeamBadge,
              },
              playedGames: parseInt(team.intPlayed),
              won: parseInt(team.intWin),
              draw: parseInt(team.intDraw),
              lost: parseInt(team.intLoss),
              points: parseInt(team.intPoints),
              goalsFor: parseInt(team.intGoalsFor),
              goalsAgainst: parseInt(team.intGoalsAgainst),
              goalDifference: parseInt(team.intGoalDifference),
              form: team.strForm || "",
            })
          );
          setStandings(transformedData);
        } else {
          throw new Error("Invalid data format");
        }
      } catch (err) {
        console.error("Error fetching standings:", err);
        setError("Failed to load Premier League standings");
        // Fallback to mock data for development
        setStandings(generateMockData());
      } finally {
        setLoading(false);
      }
    };

    fetchStandings();
  }, [open, standings.length]);

  const totalPages = Math.ceil(standings.length / ITEMS_PER_PAGE);
  const paginatedData = standings.slice(
    page * ITEMS_PER_PAGE,
    (page + 1) * ITEMS_PER_PAGE
  );

  const handlePrevPage = () => {
    if (page > 0) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) setPage(page + 1);
  };

  return (
    <>
      <Button variant="outlinedIcon" size="sm" onClick={() => setOpen(true)}>
        <Trophy className="size-3.5" />
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle className="flex items-center justify-between pr-12">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Trophy className="size-5 text-primary" />
            </div>
            <div>
              <div className="text-lg font-bold">Premier League</div>
              <div className="text-sm text-muted-foreground">
                2026/27 Season Standings
              </div>
            </div>
          </div>
          <Button
            variant="icon"
            size="sm"
            className="absolute top-3 right-3"
            onClick={() => setOpen(false)}
          >
            <X className="size-4" />
          </Button>
        </DialogTitle>
        <DialogContent>
          <div>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="size-6 animate-spin text-primary" />
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <Trophy className="size-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">{error}</p>
              </div>
            ) : (
              <div>
                {/* Table Header */}
                <div className="flex items-center gap-3 px-3 py-2.5 bg-muted/50 rounded-lg text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  <div className="w-7 text-center">#</div>
                  <div className="flex-1">Team</div>
                  <div className="w-8 text-center">MP</div>
                  <div className="w-8 text-center">W</div>
                  <div className="w-8 text-center">D</div>
                  <div className="w-8 text-center">L</div>
                  <div className="w-14 text-center">Goals</div>
                  <div className="w-10 text-center">GD</div>
                  <div className="w-[90px] text-center">Form</div>
                  <div className="w-10 text-center">Pts</div>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-border/50 mt-1">
                  {standings.map((team) => {
                    const isTopFour = team.position <= 4;
                    const isEuropaLeague = team.position === 5;
                    const isConferenceLeague = team.position === 6;
                    const isRelegation = team.position >= 18;

                    return (
                      <div
                        key={team.team.id}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/30 transition-colors rounded-lg"
                      >
                        {/* Position */}
                        <div
                          className={`flex items-center justify-center w-7 h-7 rounded-lg font-bold text-xs flex-shrink-0 ${
                            isTopFour
                              ? "bg-blue-500 text-white"
                              : isEuropaLeague
                              ? "bg-orange-500 text-white"
                              : isConferenceLeague
                              ? "bg-green-600 text-white"
                              : isRelegation
                              ? "bg-red-500 text-white"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {team.position}
                        </div>

                        {/* Team Logo & Name */}
                        <div className="flex items-center gap-2.5 flex-1 min-w-0">
                          {team.team.crest && (
                            <img
                              src={team.team.crest}
                              alt={team.team.name}
                              className="size-6 object-contain flex-shrink-0"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          )}
                          <span className="font-medium text-sm truncate">
                            {team.team.name}
                          </span>
                        </div>

                        {/* Stats */}
                        <div className="w-8 text-center text-sm text-muted-foreground">
                          {team.playedGames}
                        </div>
                        <div className="w-8 text-center text-sm text-green-600 dark:text-green-400 font-medium">
                          {team.won}
                        </div>
                        <div className="w-8 text-center text-sm text-muted-foreground">
                          {team.draw}
                        </div>
                        <div className="w-8 text-center text-sm text-red-600 dark:text-red-400 font-medium">
                          {team.lost}
                        </div>
                        <div className="w-14 text-center text-sm text-muted-foreground">
                          {team.goalsFor}-{team.goalsAgainst}
                        </div>
                        <div
                          className={`w-10 text-center text-sm font-semibold ${
                            team.goalDifference > 0
                              ? "text-green-600 dark:text-green-400"
                              : team.goalDifference < 0
                              ? "text-red-600 dark:text-red-400"
                              : "text-muted-foreground"
                          }`}
                        >
                          {team.goalDifference > 0 ? "+" : ""}
                          {team.goalDifference}
                        </div>
                        {/* Form - Last 5 matches (oldest first, newest last) */}
                        <div className="w-[90px] flex items-center justify-center gap-1">
                          {team.form
                            .split("")
                            .reverse()
                            .map((result, idx) => (
                              <div
                                key={idx}
                                className={`w-4 h-4 rounded flex items-center justify-center text-[9px] font-bold text-white ${
                                  result === "W"
                                    ? "bg-green-500"
                                    : result === "D"
                                    ? "bg-gray-400"
                                    : "bg-red-500"
                                }`}
                              >
                                {result}
                              </div>
                            ))}
                        </div>
                        <div className="w-10 text-center">
                          <span className="inline-flex items-center justify-center min-w-[32px] px-2 py-1 rounded-full bg-primary/10 text-primary font-bold text-sm">
                            {team.points}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="flex items-center justify-end gap-4 mt-4 pt-3 border-t border-border text-xs">
                  <span className="text-muted-foreground">Form:</span>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded bg-green-500 text-[9px] font-bold text-white flex items-center justify-center">
                      W
                    </div>
                    <span className="text-muted-foreground">Win</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded bg-gray-400 text-[9px] font-bold text-white flex items-center justify-center">
                      D
                    </div>
                    <span className="text-muted-foreground">Draw</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded bg-red-500 text-[9px] font-bold text-white flex items-center justify-center">
                      L
                    </div>
                    <span className="text-muted-foreground">Loss</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Mock data for development (in case API fails or for testing)
function generateMockData(): TeamStanding[] {
  const teams = [
    "Liverpool",
    "Manchester City",
    "Arsenal",
    "Chelsea",
    "Tottenham",
    "Manchester United",
    "Newcastle",
    "Brighton",
    "Aston Villa",
    "West Ham",
    "Brentford",
    "Fulham",
    "Crystal Palace",
    "Wolves",
    "Everton",
    "Nottingham Forest",
    "Bournemouth",
    "Luton Town",
    "Burnley",
    "Sheffield United",
  ];

  return teams.map((name, index) => ({
    position: index + 1,
    team: {
      id: index + 1,
      name,
      shortName: name.split(" ")[0],
      crest: "",
    },
    playedGames: 15,
    won: Math.max(0, 12 - index),
    draw: Math.floor(Math.random() * 5),
    lost: Math.floor(Math.random() * 5),
    points: Math.max(5, 45 - index * 2),
    goalsFor: Math.max(10, 40 - index * 2),
    goalsAgainst: Math.min(40, 15 + index * 2),
    goalDifference: Math.max(-20, 25 - index * 3),
    form:
      ["W", "D", "L"][Math.floor(Math.random() * 3)] +
      ["W", "D", "L"][Math.floor(Math.random() * 3)] +
      ["W", "D", "L"][Math.floor(Math.random() * 3)] +
      ["W", "D", "L"][Math.floor(Math.random() * 3)] +
      ["W", "D", "L"][Math.floor(Math.random() * 3)],
  }));
}
